import { type MouseEvent, type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PaletteUtils } from '@shared/utils';
import { Button } from '@shared/widgets';

interface TableProps<T> {
  columns?: ExtendedColumnType<T>[];
  data: T[];
  idAttribute?: keyof T;
  className?: string;
  loading?: boolean;
  subLoading?: boolean;
  subLoadingClass?: string;
  isMobile?: boolean;
  showPaginate?: boolean;
  count?: number;
  from?: number;
  emptyText?: ReactNode;
  title?: string;
  showHeader?: boolean;
  onNextPage?: (from: number) => void;
  onRow?: (record: T, index: number) => { onClick: (e: MouseEvent) => void };
}

interface ExtendedColumnType<T> {
  title?: ReactNode;
  dataIndex?: string;
  key?: string;
  render?: (value: any, context: { record: T; index: number }) => ReactNode;
  width?: number | string;
  className?: string;
  mobile?: boolean;
}

export const Table = <T extends object>(props: TableProps<T>) => {
  const { t } = useTranslation();

  const isEmpty = !props.data?.length;
  const isShowHeader = !isEmpty && (props.showHeader !== undefined ? props.showHeader : !props.isMobile);

  const transformedData = props.data?.map((record, index) => ({ ...record, key: props.idAttribute ? record[props.idAttribute] : index }));

  const renderCell = useCallback(
    (col: ExtendedColumnType<T>) => (record: T, index: number) => {
      const value = col.dataIndex ? (record as any)[col.dataIndex] : undefined;
      return col.render ? col.render(value, { record, index }) : value;
    },
    [props.isMobile],
  );

  const adaptedColumns = props.columns
    ?.filter((col) => col.mobile === undefined || props.isMobile === !!col.mobile)
    .map((col) => ({ ...col, key: col.key || col.dataIndex }));

  const handleRowClick = useCallback(
    (record: T, index: number) => {
      const rowProps = props.onRow?.(record, index) || {};
      return (e: React.MouseEvent) => {
        //@ts-ignore
        rowProps.onClick?.(e);
      };
    },
    [props.isMobile, props.onRow],
  );

  return (
    <div className={clsx('bg-[#fcfcfc]', props.className)}>
      {props.title && (
        <div>
          <div
            className={clsx(
              'text-[20px] font-[900] uppercase mb-[23px] mt-[-50px]',
              PaletteUtils.isBordierPalette ? 'text-[#dc291c]' : 'text-[#be8b45]',
            )}
          >
            {props.title}
          </div>
        </div>
      )}
      <div className='table-content overflow-x-auto'>
        {props.loading ? null : isEmpty ? (
          props.emptyText
        ) : (
          <>
            <table className='table-fixed border-separate border-spacing-0 w-full border border-[#e1e1e1]'>
              {isShowHeader && (
                <thead>
                  <tr>
                    {adaptedColumns?.map((column, index) => (
                      <th
                        key={index}
                        className={clsx(
                          'text-[15px] text-left whitespace-nowrap py-4 px-7 leading-[1.27] w-full',
                          PaletteUtils.isBordierPalette
                            ? 'border-b border-b-[#e1e1e1] font-medium text-[#4c4c52] bg-[#f8f8f8]'
                            : 'font-semibold text-[#6b6b70] bg-[#eaeaea]',
                        )}
                        style={{ width: column.width }}
                      >
                        {column.title}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {transformedData?.map((record, rowIndex) => (
                  <tr
                    key={record.key as string}
                    className={clsx('[&:not(:last-child)]:!border-b-[#e1e1e1] even:!bg-white odd:!bg-white relative')}
                    onClick={handleRowClick(record, rowIndex)}
                  >
                    {adaptedColumns?.map((column, index) => (
                      <td
                        key={index}
                        className={clsx(
                          'text-sm text-left text-ellipsis overflow-hidden whitespace-nowrap border-l-[1px] first:!border-l-0 border-l-[#e5e5e5] py-2.5 px-7 max-[840px]:text-[#27272b] w-full',
                          PaletteUtils.isBordierPalette
                            ? 'bg-white !font-light border-b-[#e1e1e1] !border-l-0 border-b-[1px]'
                            : rowIndex % 2 === 1
                              ? 'bg-[#eaeaea] max-[840px]:border-0'
                              : 'bg-[#fcfcfc] max-[840px]:border-0',
                          'font-black',
                          column.className,
                          !column.mobile && props.isMobile && 'max-[840px]:hidden',
                        )}
                        style={{ width: column.width }}
                      >
                        {renderCell(column)(record, rowIndex)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {props.showPaginate && !isEmpty && (
              <div
                className={clsx('flex justify-center pt-4 px-5', PaletteUtils.isBordierPalette ? 'bg-white' : 'bg-[#27272b]', props.subLoadingClass)}
              >
                <Button
                  className='min-w-[140px]'
                  caption={t('buttons.loadMore')}
                  onClick={() => props.onNextPage?.(props.from! + 20)}
                  disabled={props.subLoading || isEmpty}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
